export const theme = {
  fonts: {
    display: "'Exo 2', sans-serif",
    body: "'Figtree', sans-serif",
    // Almanac display font (VISUAL_OVERHAUL §"Typography"). Used for the
    // hero stat, league name, section headers. System sans falls back when
    // Inter hasn't loaded yet to avoid FOIT.
    almanac: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  },

  colors: {
    brand: {
      orange: "#FF6B00",
      accent: "#FF9500",
      deep: "#E55A00",
      gradient: "linear-gradient(135deg, #FF6B00, #FF9500)",
      gradientBtn: "linear-gradient(135deg, #FF6B00, #E55A00)",
    },
    // Almanac section category accents (VISUAL_OVERHAUL §"Category theming").
    // Used ONLY on the 4px left border of a Section row + the caret icon —
    // never as a full background. Subtle visual differentiation per section
    // type without chromatic chaos.
    accents: {
      gold:   "#d4a72c",   // Championships, Playoffs, Awards
      blue:   "#3b82f6",   // Drafts
      green:  "#10b981",   // Trades
      purple: "#a855f7",   // Career Totals
      red:    "#ef4444",   // Single-Game Records, Single-Season Records
      orange: "#f97316",   // Streaks
      cyan:   "#06b6d4",   // Head-to-Head
      yellow: "#eab308",   // Luck
      pink:   "#ec4899",   // Volatility, YoY Swings
    },
    /**
     * "My guys" — the star: the card portal's toggle, the Draft Plan list
     * toggle and the plan card's left edge bar.
     *
     * accents.gold, so the marker matches the accent the rest of the app
     * already uses rather than introducing a hue nothing else speaks.
     *
     * 🔴 THE MEASURED OBJECTION STANDS AND IS ANSWERED BY FORM, NOT HUE. Gold
     * sits ΔE 16.3 (CIE76) from TE yellow (#fbbf24), and no colour in the gold
     * family does better — #eab308 is 5.0, #facc15 is 9.6, #f59e0b is 16.8 and
     * closes on brand orange too. What keeps them apart is shape: the star is a
     * 3px edge bar or a bordered glyph button, and the position marker is a
     * filled chip with text in it. Those read as different KINDS of thing at
     * the same hue. The one case worth re-checking on screen is a starred TE
     * card; if the bar reads as a position there, this is the line to change.
     */
    star: "#d4a72c",
    /**
     * "I won him" — the lock: the Draft Plan target card's padlock, its border
     * and its LOCKED badge.
     *
     * 🔴 CHOSEN AGAINST THE STAR, NOT IN ISOLATION. Both marks can sit on the
     * same card at once — a starred player you then went and bought — so the
     * pair has to separate at a glance, in the dark, from across a table. Gold
     * (#d4a72c) is warm and saturated; this is a cool neutral steel at ΔE 44
     * (CIE76) from it, which no hue in the gold or orange family gets near.
     *
     * 🔴 AND NEUTRAL IS THE POINT, not a lack of imagination. Every saturated
     * colour on this card is already spoken for: orange is PRIMARY, gold is the
     * star, and the six position chips own red/green/blue/yellow/purple/pink. A
     * seventh saturated accent would collide with one of them on some card. A
     * metal reads as a padlock and competes with nothing.
     *
     * If a brighter mark is ever wanted, this token and lockBg below are the
     * only two places to change — nothing hand-writes the value.
     */
    lock: "#b8c4d4",
    bg: {
      app: "#060a12",
      deep: "#0a0f1a",
      surface: "#0d1320",
      raised: "#1a2332",
      elevated: "#1e293b",
    },
    text: {
      primary: "#e2e8f0",
      secondary: "#A0AEC0",
      muted: "#94a3b8",
      faint: "#718096",
    },
    border: {
      default: "#334155",
      subtle: "rgba(255,255,255,0.06)",
      brand: "rgba(255,107,0,0.3)",
      brandLight: "rgba(255,107,0,0.2)",
    },
    status: {
      success: "#22c55e",
      danger: "#ef4444",
      warning: "#eab308",
      info: "#60a5fa",
    },
    pos: {
      QB: "#f87171",
      RB: "#34d399",
      WR: "#60a5fa",
      TE: "#fbbf24",
      K: "#a78bfa",
      DEF: "#f472b6",
    },
    posBg: {
      QB: "rgba(239,68,68,0.15)",
      RB: "rgba(52,211,153,0.15)",
      WR: "rgba(96,165,250,0.15)",
      TE: "rgba(251,191,36,0.15)",
      K: "rgba(167,139,250,0.15)",
      DEF: "rgba(244,114,182,0.15)",
    },
    posBorder: {
      QB: "rgba(239,68,68,0.25)",
      RB: "rgba(52,211,153,0.25)",
      WR: "rgba(96,165,250,0.25)",
      TE: "rgba(251,191,36,0.25)",
      K: "rgba(167,139,250,0.25)",
      DEF: "rgba(244,114,182,0.25)",
    },
    extended: {
      emerald: "#34d399",
      copied: "#4ade80",
      valueHighlight: "#FFB84D",
      resetDanger: "#7f1d1d",
      resetDangerText: "#fca5a5",
      modalBg: "#0f172a",
      modalBgDeep: "#131b2e",
    },
  },

  spacing: { xs: 4, sm: 6, md: 8, lg: 12, xl: 16, xxl: 20, xxxl: 24, huge: 32, massive: 40 },

  radius: { sm: 6, md: 8, lg: 10, xl: 12, xxl: 14 },

  // Floor is micro(10) for readable text; nano(9) is reserved ONLY for uppercase letter-spaced badges/overlines.
  fontSize: { nano: 9, micro: 10, caption: 11, small: 12, body: 13, sub: 14, base: 16, heading: 18, title: 22, display: 24, hero: 26 },

  fontWeight: { regular: 400, normal: 500, medium: 600, bold: 700, heavy: 800, black: 900 },
};

theme.helpers = {
  posStyle: (pos) => ({
    color: theme.colors.pos[pos],
    background: theme.colors.posBg[pos],
    border: `1px solid ${theme.colors.posBorder[pos]}`,
  }),
  brandGlow: (intensity = 0.3) => `0 4px 20px rgba(255,107,0,${intensity})`,
  overlay: (opacity = 0.6) => `rgba(0,0,0,${opacity})`,
  brandBg: (opacity = 0.08) => `rgba(255,107,0,${opacity})`,
  // Same shape as brandBg, and it exists so the star's tinted backgrounds are
  // not hand-written rgba literals that stop tracking colors.star the moment
  // anyone edits it. 🔴 Change BOTH together — this is colors.star in rgb.
  starBg: (opacity = 0.14) => `rgba(212,167,44,${opacity})`,
  // Same shape and the same warning as starBg. 🔴 This is colors.lock in rgb —
  // change BOTH together.
  lockBg: (opacity = 0.12) => `rgba(184,196,212,${opacity})`,
};

export default theme;
export const { colors, fonts, spacing, radius, fontSize, fontWeight, helpers } = theme;
